<template>
  <!-- TODO: input-group--error if errors -->
  <div class="nx-upload">

    <div class="dropzone blue lighten-5" v-show="$refs.upload && $refs.upload.dropActive"></div>

    <v-snackbar
      :timeout="0"
      :value="$refs.upload && $refs.upload.dropActive"
      bottom
    >
      {{ $tc('message.upload.drophere', 1) }}
      <v-btn flat>
        <v-icon color="accent">file_upload</v-icon>
      </v-btn>
    </v-snackbar>
    <v-text-field
      :error-messages="errorMessages"
      :error="!valid"
      :messages="warning || []"
      :warning="true"
      :hint="hintText"
      :label="label"
      :loading="warning ? true : $refs.upload && ($refs.upload.dropActive || $refs.upload.active)"
      :value="textValue"
      append-icon="file_upload"
      @click:append="browse"
      @focus="$event.target.select()"
      @input="onInputTextValue"
      name="textValue"
      type="text"
      persistent-hint
    >
      <template v-slot:progress>
        <v-progress-linear
          v-if="warning"
          :value="100"
          :color="'warning'"
          height="1"
        ></v-progress-linear>
      </template>
      <template v-slot:prepend-inner>
        <v-icon class="warning--text" v-show="warning">warning</v-icon>
      </template>
      <template v-slot:append-outer>
        <v-tooltip top class="text-xs-center">
          <v-icon v-show="showResizeTool" slot="activator" @click="openResizeTool">crop</v-icon>
          <span>{{$t('message.clipping.selector.crop')}}</span>
        </v-tooltip>
      </template>
    </v-text-field>
    <div class="upload">
      <file-upload
        :accept="uploadOptions.accept"
        :data="uploadData"
        :directory="uploadOptions.directory"
        :drop-directory="uploadOptions.dropDirectory"
        :drop="uploadOptions.drop"
        :extensions="uploadOptions.extensions"
        :headers="headers"
        :maximum="uploadOptions.maximum"
        :multiple="uploadOptions.maximum > 1"
        :name="name"
        :post-action="uploadOptions.url"
        :size="uploadOptions.size"
        @input-file="inputFile"
        @input-filter="inputFilter"
        class="file-upload"
        ref="upload"
        :value="files"
        @input="acceptFiles"
      >
        <label class="label" :for="name" ref="uploadLabel"></label>
      </file-upload>
    </div>

    <div class="limit-wrapper">
      <span class="limit-counter grey--text"
            :class="{ reached: this.limit < files.length }"
            v-if="limit > 1 && limitCounter">
        {{ files.length }}/{{limit }}
      </span>
    </div>

    <nx-image-focal-point
      v-if="focalPointEnabled"
      :src="selectedFileBlob"
      v-model="internalFocalPoint"
      name="focalPoint"
      :disabled="!selectedFileBlob"
    ></nx-image-focal-point>

    <nx-clipping-tool-cropper
      v-if="selectedFileIsImage"
      :active="cropTool"
      :aspectRatios="beforeUploadClippingsConfig"
      :src="originalImageBlob || selectedFileBlob"
      @crop="acceptResize"
      @dismiss="dismissResizeTool"
      ref="cropper"
      :title="$t('message.clipping.clipper.resize')"
    ></nx-clipping-tool-cropper>
    <div v-if="options.maximum === 1 && (selectedFileIsImage || selectedFileIsVideo)" class="preview mt-3">
      <nx-file-upload-preview :file="selectedFile && selectedFile.file"
                              :internal-focal-point="internalFocalPoint"
                              dropzone
                              @preview:load="onPreviewLoad" />
    </div>
    <div class="multiple-preview" v-else-if="selectedFileIsImage">
      <v-layout>
        <v-flex class="mt10">
          <v-container v-bind="{ [`grid-list-lg`]: true }" fluid>
            <v-layout row wrap>
              <v-flex v-for="file in files" :key=file.id xs4>
                <v-card flat tile>
                  <v-img height="150px" :src="file.blob" :contain="true"></v-img>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-flex>
      </v-layout>
    </div>

  </div>
</template>

<script>
import Toast from '@/modules/toast/services';
import VueUploadComponent from '@/plugins/vue-upload-component';
import Loadable from '@/mixins/loadable';
import { mapGetters } from 'vuex';
import api from './api';
import uuid from 'uuid/v4';
import NxImageFocalPoint from '@/modules/form/components/ImageFocalPoint';
import NxClippingToolCropper from '@/modules/clipping/components/Cropper';
import { validImageExtensions, imageMinSizeQuality } from '@/modules/file/config';
import NxFileUploadPreview from '@/modules/upload/FileUploadPreview';
import { cropToAspectRatio } from '@/modules/upload/utils';
import { mimeTypeMatch } from '@/utils';

export default {
  name: 'nx-upload-file',
  mixins: [Loadable],
  components: {
    NxFileUploadPreview,
    NxImageFocalPoint,
    FileUpload: VueUploadComponent,
    NxClippingToolCropper,
  },
  props: {
    label: {
      type: String,
    },
    name: {
      type: String,
      required: true,
    },
    // Post data on upload
    data: {
      type: Object,
      required: false,
    },
    options: {
      type: Object,
    },
    limitCounter: {
      type: Boolean,
      default: true,
    },
    beforeUploadClippingsConfig: {
      type: Array,
      default: () => [],
    },
    beforeUploadForceClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadAutomaticClip: {
      type: Boolean,
      default: false,
    },
    manualHintText: {
      type: String,
    },
  },
  data() {
    return {
      originalImageFile: null,
      warning: null,
      previewBadge: '',
      previewLoaded: false,
      textValue: null,
      valid: true,
      errorMessages: [],
      files: [],
      internalFocalPoint: null,
      cropTool: false,
      imageMustBeCropped: false,
      defaultOptions: {
        maximum: 0,
        accept: '*',
        extensions: undefined,
        directory: false,
        size: 0,
        drop: true,
        dropDirectory: true,
      },
    };
  },
  computed: {
    originalImageBlob() {
      if (!this.originalImageFile) return null;
      return URL.createObjectURL(this.originalImageFile);
    },
    ...mapGetters(['context', 'availableClippingAspectRatios']),
    empty() {
      return this.files.length === 0;
    },
    limit() {
      return this.uploadOptions.maximum;
    },
    uploadOptions() {
      return {
        ...this.defaultOptions,
        ...this.options,
      };
    },
    uploadMimeTypeAccept() {
      return this.uploadOptions.accept;
    },
    uploadingImages() {
      return this.uploadOptions.accept && this.uploadOptions.accept.includes('image');
    },
    uploadingVideos() {
      return this.uploadOptions.accept && this.uploadOptions.accept.includes('video');
    },
    uploadData() {
      const payload = {
        ...this.data,
        context: this.context,
      };
      if (this.internalFocalPoint) {
        payload.focalPoint = this.internalFocalPoint;
      }
      return {
        payload: JSON.stringify(payload),
      };
    },
    dropActive() {
      return this.$refs.upload && this.$refs.upload.dropActive;
    },
    headers() {
      return {
        'X-SITE-ID': this.$store.getters.siteId,
        'X-Request-Id': uuid(),
      };
    },
    hintText() {
      if (this.manualHintText) {
        return this.manualHintText;
      }
      if (this.uploadingImages) {
        return this.$t('message.upload.selectImageWithDragAndDropSupport');
      }
      return this.$t('message.upload.selectFile');
    },
    sizeInKb() {
      if (this.size > 0) {
        return null;
      }
      return Math.floor(this.uploadOptions.size / 1024);
    },
    focalPointEnabled() {
      return this.uploadOptions.focalPointEnabled
        && this.uploadingImages && this.limit === 1 && this.selectedImage;
    },
    selectedFile() {
      return (this.files && this.files[0]) || null;
    },
    selectedFileBlob() {
      return (this.selectedFile && this.selectedFile.blob) || null;
    },
    selectedFileType() {
      return (this.selectedFile && this.selectedFile.type) || null;
    },
    selectedFileIsImage() {
      return this.selectedFileType && this.selectedFileType.startsWith('image');
    },
    selectedFileIsVideo() {
      return this.selectedFileType && this.selectedFileType.startsWith('video');
    },
    selectedImage() {
      return (this.uploadingImages && this.selectedFile) || null;
    },
    showResizeTool() {
      return this.uploadingImages
        && !this.empty
        && this.selectedFileIsImage
        && this.beforeUploadClippingsConfig.length > 0;
    },
  },
  methods: {
    acceptFiles(files) {
      this.files = files;
    },
    acceptResize(crop, canvas) {
      this.imageMustBeCropped = false;
      // Transform the canvas to a blob
      canvas.toBlob((blob) => {
        // Transform the blob to a file
        const file = new File([blob], this.selectedImage.file.name, {
          type: blob.type,
        });

        // Configure the file as the new file to upload
        this.removeFile(this.selectedImage.file);
        this.addFile(file);
        this.closeResizeTool();
      }, this.selectedImage.file.type, 0.8);
    },
    dismissResizeTool() {
      if (this.imageMustBeCropped && this.selectedImage && this.selectedImage.file) {
        this.clear();
        this.imageMustBeCropped = false;
      }
      this.closeResizeTool();
    },
    openResizeTool() {
      this.cropTool = true;
    },
    closeResizeTool() {
      this.$refs.cropper.reset();
      this.cropTool = false;
    },
    onPreviewLoad(image) {
      // It sucks, like a lot, to do this here, but loading the image is asyncronous and we can't use an event to
      // calculate this Since the data we need is on the badge, we leech this method to calculate the warning
      if (image.naturalHeight < imageMinSizeQuality.height
        || image.naturalWidth < imageMinSizeQuality.width
      ) {
        this.warning = this.$t('message.upload.warning.minWidthHeight', { width: imageMinSizeQuality.width, height: imageMinSizeQuality.height });
      }
    },
    async createFileFromURL(url) {
      const blob = await api.getImageBlob(url);
      const pathname = new URL(url).pathname || url;
      return new File([blob], pathname.substring(pathname.lastIndexOf('/') + 1), { type: 'image/jpeg' });
    },
    async onInputTextValue(value) {
      if (!this.uploadingImages) {
        // Nothing to do here if we are not uploading an image
        return;
      }
      try {
        const parsedURL = new URL(value);
        if (!validImageExtensions.some(extension => parsedURL.pathname.endsWith(extension))) {
          // Not a valid image extension
          this.clear();
          this.valid = false;
          this.errorMessages.push(this.$t('message.upload.error.unsupportedFile'));
          return;
        }
        const file = await this.createFileFromURL(value);
        this.addFile(file);
        this.textValue = value;
      } catch (e) {
        this.clear();
        this.valid = false;
        this.errorMessages.push(this.$t('message.upload.error.invalidURL'));
      }
    },
    /**
     * Has changed
     * @param  {Object|undefined}   newFile   Read only
     * @param  {Object|undefined}   oldFile   Read only
     * @return undefined
     */
    async inputFile(newFile, oldFile) {
      console.log('input file', { newFile, oldFile });
      this.warning = null;
      // Reset state
      this.errorMessages = [];
      this.valid = true;

      // Change status because of active change
      if (newFile && oldFile) {
        if (newFile.name !== oldFile.name) {
          this.internalFocalPoint = null;
        }
        if (newFile.active && !oldFile.active) {
          // File starts submit
          this.loadingStart();
        } else if (!newFile.active && oldFile.active) {
          // File uploaded finish
          this.loadingFinish();
          if (newFile.xhr
            && [200, 201].includes(newFile.xhr.status)) {
            this.$emit('success', newFile);
          } else {
            this.errorMessages.push(this.$t(`message.upload.error.${newFile.error || 'unknown'}`));
          }
        }
      }

      // Show errors and set invalid state on errors
      if (newFile && newFile.error) {
        this.valid = false;
        this.errorMessages.push(this.$t(`message.upload.error.${newFile.error}`));
      }
      if (newFile && newFile.size > this.uploadOptions.size) {
        this.valid = false;
        this.errorMessages.push(this.$t('message.upload.error.size', { size: this.sizeInKb }));
      }

      // Set input text
      if (newFile) {
        this.textValue = newFile.name;
        this.$emit('newFile', newFile);
      }
    },
    /**
     * Pretreatment
     * @param  {Object|undefined}   newFile   Read and write
     * @param  {Object|undefined}   oldFile   Read only
     * @param  {Function}           prevent   Prevent changing
     */
    async inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        let extensions = validImageExtensions.join('|');
        if (this.options.accept.includes('video/mp4')) {
          extensions = extensions.concat('|mp4');
        }
        const regexp = new RegExp(`.(${extensions})`, 'i');
        if (this.uploadingImages
          && !regexp.test(newFile.name)) {
          Toast.notify('message.upload.error.unsupportedFile');
          return prevent();
        }
        const file = newFile.file;
        // Verify with mime accept
        if (file.type
            && this.uploadMimeTypeAccept
            && !mimeTypeMatch(file.type, this.uploadMimeTypeAccept)) {
          Toast.notify('message.upload.error.unsupportedFile');
          return prevent();
        }
        // Create a blob field for preview
        if (!newFile.blob) {
          this.$set(newFile, 'blob', URL.createObjectURL(newFile.file));
        }
      }

      // Update the file data before upload it
      if (newFile && oldFile && newFile.active && !oldFile.active) {
        const { payload } = this.uploadData;
        newFile.data.payload = payload;
      }

      if (!this.uploadingImages) {
        return true;
      }

      if (!newFile) {
        return true;
      }

      // Automatic clip images before upload
      const uploadingDifferentFile = newFile.blob !== (oldFile && oldFile.blob);
      const fileIsAutomaticClipping = newFile.file.automaticCrop === true;
      const automaticCropRatio = this.availableClippingAspectRatios[this.beforeUploadClippingsConfig[0]];
      if (this.beforeUploadAutomaticClip
        && automaticCropRatio
        && uploadingDifferentFile
        && !fileIsAutomaticClipping) {
        // Save original clip reference
        this.originalImageFile = new File([newFile.file], newFile.name, { type: newFile.type });
        try {
          prevent();
          const crop = await cropToAspectRatio(newFile.file, automaticCropRatio.width / automaticCropRatio.height);
          // Configure the file as the new file to upload
          this.$set(crop, 'automaticCrop', true);
          this.addFile(crop);
          return true;
        } catch (e) {
          console.error(e);
        }
      }

      // Force manual crop
      if (this.beforeUploadForceClip
        && uploadingDifferentFile
        && !fileIsAutomaticClipping) {
        this.imageMustBeCropped = true;
        this.openResizeTool();
      }

      return true;
    },
    browse() {
      if (!this.loading) {
        this.$refs.uploadLabel.click();
      }
    },
    clear() {
      this.errorMessages = [];
      this.valid = true;
      this.textValue = null;
      this.previewBadge = null;
      this.warning = null;
      this.$refs.upload.clear();
    },
    addFile(file) {
      this.$refs.upload.add(file);
    },
    removeFile(file) {
      this.$refs.upload.remove(file);
    },
    submit() {
      this.$nextTick(() => {
        this.$refs.upload.active = true;
      });
    },
  },
};
</script>

<style lang="stylus" scoped>
.nx-upload {

  .dropzone {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 1;
    opacity: .6;
    transition: background-color .2s linear;
  }

  .label {
    display: none;
  }

  .file-upload {
    display: none;
  }

  .limit-wrapper {
    position: relative;

    .limit-counter {
      position: absolute;
      right: 0;
      bottom: 0;
      font-size: 12px;

      &.reached {
        color: red;
      }
    }
  }
}

</style>
